/**
 * Liked Videos Service
 *
 * Domain layer service for the liked-videos feature. Reads the local
 * UserMediaLike records from encrypted storage and applies Flutter's
 * snapshot-time filters (liked && !hiddenContent) plus the newest-first
 * sort.
 *
 * Singleton pattern for consistent state and efficient resource usage —
 * mirrors UnlockedVideosService and the other domain service singletons.
 *
 * Flutter parity:
 * - lib/presentation/screens/content/liked_content_screen.dart:69-92
 *   (getUserMediaLikes: read Hive UserMediaLike box, filter
 *    `liked == true && !currentUser.hiddenContent.contains(mediaId)`,
 *    sort descending by createdDate).
 */

import { SecureStorageWebCrypto } from '@/data/storage/local/secureStorageWebCrypto';
import { SECURE_STORAGE_KEYS } from '@/domain/entities/secureStorage/secureStorageKeys';
import type { UserMediaLikeStorage } from '@/domain/entities/secureStorage/secureStorageEntities';
import type { UserEntity } from '@/domain/entities/user/userEntity';

export class LikedVideosService {
  private static instance: LikedVideosService;

  private constructor() { }

  public static getInstance(): LikedVideosService {
    if (!LikedVideosService.instance) {
      LikedVideosService.instance = new LikedVideosService();
    }
    return LikedVideosService.instance;
  }

  /**
   * Read all locally-stored UserMediaLike records that the current user
   * actively likes, sorted newest first.
   *
   * Filters:
   * - `liked === false` records are dropped (Flutter line 83).
   * - records whose `mediaId` appears in `currentUser.hiddenContent`
   *   are dropped (Flutter line 84). A null/undefined `currentUser` or
   *   missing `hiddenContent` keeps everything (Flutter coalesces with
   *   `?? []`).
   *
   * Sort: descending by `createdDate` — Flutter line 88's
   * `a.createdDate < b.createdDate ? 1 : 0` pushes older entries down,
   * which is effectively newest-first.
   *
   * `currentUser` is taken as a parameter; domain services must not read
   * Redux directly (per `.claude/context/conventions.md`).
   *
   * Mirrors Flutter's getUserMediaLikes in
   * lib/presentation/screens/content/liked_content_screen.dart:69-92.
   */
  async listLocalLikedVideos(
    currentUser: UserEntity | null,
  ): Promise<UserMediaLikeStorage[]> {
    const storage = SecureStorageWebCrypto.getInstance();
    const items = await storage.getAllWithPrefix<UserMediaLikeStorage>(
      SECURE_STORAGE_KEYS.LIKES,
    );

    const hiddenContent = currentUser?.hiddenContent ?? [];

    const filtered = items.filter(
      (item) => item.liked && !hiddenContent.includes(item.mediaId),
    );

    return filtered.sort((a, b) => b.createdDate - a.createdDate);
  }
}

// Export singleton instance for convenience
export const likedVideosService = LikedVideosService.getInstance();
